/**
 * Parses a query string into an object
 * @param {string} queryString - The query string to parse (with or without leading '?')
 * @returns {Object} The parsed query string as an object
 * @example
 * // returns { foo: 'bar', baz: 'qux' }
 * parse('foo=bar&baz=qux')
 *
 * // returns { foo: ['bar', 'baz'] }
 * parse('foo[0]=bar&foo[1]=baz')
 *
 * // returns { foo: { bar: 'baz' } }
 * parse('foo.bar=baz')
 */
function parse(queryString) {
  if (!queryString || typeof queryString !== "string") {
    return {};
  }

  const cleanQueryString = removeLeadingQuestionMark(queryString);
  const result = {};

  cleanQueryString.split("&").forEach((param) => {
    if (!param) return;
    parseAndAssignParam(param, result);
  });

  return result;
}

function removeLeadingQuestionMark(queryString) {
  return queryString.charAt(0) === "?" ? queryString.slice(1) : queryString;
}

function parseAndAssignParam(param, result) {
  const [path, value] = splitParamIntoPathAndValue(param);
  const parts = splitPathIntoParts(path);
  assignValueToPath(parts, value, result);
}

function splitParamIntoPathAndValue(param) {
  const [path, value] = param.split("=");
  const decodedValue = decodeURIComponent(value || "");
  return [path, decodedValue];
}

function splitPathIntoParts(path) {
  return path
    .split(/[[\.]/)
    .map((part) => part.replace("]", "").trim())
    .filter(Boolean);
}

function assignValueToPath(parts, value, result) {
  let current = result;

  parts.forEach((part, index) => {
    const isLastPart = index === parts.length - 1;

    if (isLastPart) {
      current[part] = value;
      return;
    }

    const nextPart = parts[index + 1];
    const shouldBeArray = Number.isInteger(Number(nextPart));

    if (!current[part]) {
      current[part] = shouldBeArray ? [] : {};
    }

    current = current[part];
  });
}

/**
 * Converts an object into a query string
 * @param {Object} obj - The object to stringify
 * @param {Object} [options] - Options for stringification
 * @param {boolean} [options.allowDots=false] - Use dot notation instead of bracket notation
 * @returns {string} The stringified query string
 * @example
 * // returns 'foo=bar&baz=qux'
 * stringify({ foo: 'bar', baz: 'qux' })
 *
 * // returns 'foo[0]=bar&foo[1]=baz'
 * stringify({ foo: ['bar', 'baz'] })
 *
 * // with allowDots=true returns 'foo.bar=baz'
 * // with allowDots=false returns 'foo[bar]=baz'
 * stringify({ foo: { bar: 'baz' } }, { allowDots: true })
 */
function stringify(obj, options = {}) {
  if (!obj || typeof obj !== "object") {
    return "";
  }

  const { allowDots = false } = options;

  // Add a WeakSet to track processed objects
  const seen = new WeakSet();

  function stringifyValue(key, value, parentKey = "") {
    // Check for circular references
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) {
        return ""; // Skip circular reference
      }
      seen.add(value);
    }

    if (Array.isArray(value)) {
      return value
        .map((v, i) => {
          const arrayKey = `${key}[${i}]`;
          if (typeof v === "object" && v !== null) {
            return Object.keys(v)
              .map((k) => stringifyValue(k, v[k], arrayKey))
              .join("&");
          }
          return `${arrayKey}=${encodeURIComponent(v || "")}`;
        })
        .filter(Boolean) // Filter out empty strings from circular refs
        .join("&");
    }

    if (typeof value === "object" && value !== null) {
      return Object.keys(value)
        .map((k) => {
          const newKey = parentKey
            ? `${parentKey}[${k}]`
            : allowDots
              ? `${key}.${k}`
              : `${key}[${k}]`;
          return stringifyValue(newKey, value[k], "");
        })
        .filter(Boolean) // Filter out empty strings from circular refs
        .join("&");
    }

    const finalKey = parentKey ? `${parentKey}[${key}]` : key;
    return `${finalKey}=${encodeURIComponent(value || "")}`;
  }

  return Object.keys(obj)
    .map((key) => {
      const encodedKey = encodeURIComponent(key);
      const value = obj[key];
      if (value === null || value === undefined) {
        return `${encodedKey}=`;
      }
      return stringifyValue(encodedKey, value);
    })
    .filter(Boolean)
    .join("&");
}

export { parse, stringify };
